import type { ReactNode } from 'react';
import { useState } from 'react';
import { Spinner } from 'src/components/ui/spinner';
import { VirtualList } from 'src/components/ui/virtual-list';
import {
  HEADER_ROW_HEIGHT,
  ITEM_ROW_HEIGHT
} from 'src/layouts/SidebarV2/constants';
import type { UseSidebarRows } from 'src/layouts/SidebarV2/hooks';
import { GroupHeader } from 'src/layouts/SidebarV2/primitives';
import type { SidebarRow } from 'src/layouts/SidebarV2/types';

import { ChatRow } from './ChatRow';
import { FavouriteRow } from './FavouriteRow';

export type VirtualRowListProps = UseSidebarRows;

interface RowHandlers {
  onStartFavouriteChat: VirtualRowListProps['onStartFavouriteChat'];
  onToggleFavourite: VirtualRowListProps['onToggleFavourite'];
  onRenameChat: VirtualRowListProps['onRenameChat'];
  onDeleteChat: VirtualRowListProps['onDeleteChat'];
}

const rowRenderers: {
  [K in SidebarRow['type']]: (
    row: Extract<SidebarRow, { type: K }>,
    handlers: RowHandlers
  ) => ReactNode;
} = {
  groupHeader: (row) => (
    <GroupHeader
      heading={row.heading}
      expanded={!row.collapsed}
      onToggle={row.onToggle}
    />
  ),
  favourite: (row, { onStartFavouriteChat, onToggleFavourite }) => (
    <FavouriteRow
      item={row.item}
      onStartChat={onStartFavouriteChat}
      onToggleFavourite={onToggleFavourite}
    />
  ),
  chat: (row, { onRenameChat, onDeleteChat }) => (
    <ChatRow chat={row.chat} onRename={onRenameChat} onDelete={onDeleteChat} />
  )
};

const LOADING_ROW_KEY = '__history-loading__';

const VirtualRowList = ({
  rows,
  groupRanges,
  hasMore,
  isFetching,
  loadMoreRows,
  onStartFavouriteChat,
  onToggleFavourite,
  onRenameChat,
  onDeleteChat
}: VirtualRowListProps) => {
  const [startIndex, setStartIndex] = useState(0);

  const handlers: RowHandlers = {
    onStartFavouriteChat,
    onToggleFavourite,
    onRenameChat,
    onDeleteChat
  };

  if (!isFetching && rows.length === 0) {
    return null;
  }

  /* `startIndex` comes from VirtualList's own virtualizer callback and can
   * lag one render behind `groupRanges` (recomputed synchronously from
   * `collapsed`, e.g. right after a toggle shrinks the row count). Clamping
   * against the current row count keeps a stale, too-large index from
   * matching a group's now-shrunk range and rendering the sticky header on
   * top of the wrong row for a frame. */
  const clampedStartIndex = Math.min(startIndex, rows.length - 1);
  const stickyGroup = groupRanges.find(
    (group) =>
      clampedStartIndex > group.headerIndex &&
      clampedStartIndex <= group.endIndex
  );

  return (
    <div className="tw:relative tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:w-full">
      <VirtualList<SidebarRow>
        data={rows}
        getItemKey={(row) => row.id}
        estimateSize={(index) =>
          rows[index]?.type === 'groupHeader'
            ? HEADER_ROW_HEIGHT
            : ITEM_ROW_HEIGHT
        }
        overscan={8}
        renderItem={(row) => {
          const renderer = rowRenderers[row.type] as (
            row: SidebarRow,
            handlers: RowHandlers
          ) => ReactNode;
          return renderer(row, handlers);
        }}
        trailingRows={
          isFetching
            ? [
                {
                  key: LOADING_ROW_KEY,
                  render: () => (
                    <div className="tw:flex tw:justify-center tw:py-s">
                      <Spinner size="sm" showText={false} />
                    </div>
                  )
                }
              ]
            : undefined
        }
        gateReachedOnScroll
        onEndReached={() => {
          if (hasMore) {
            loadMoreRows();
          }
        }}
        onRangeChange={(nextStartIndex) => setStartIndex(nextStartIndex)}
      />
      {stickyGroup ? (
        <div className="tw:absolute tw:left-0 tw:right-0 tw:top-0 tw:z-10 tw:bg-main">
          <GroupHeader
            heading={stickyGroup.heading}
            expanded={!stickyGroup.collapsed}
            onToggle={stickyGroup.onToggle}
          />
        </div>
      ) : null}
    </div>
  );
};

export { VirtualRowList };
